/**
 * React Query hooks for the Annotation/Issues system.
 *
 * Provides data fetching and mutation hooks for annotations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import * as annotationApi from '@/lib/annotationApi'
import type {
  AnnotationFilters,
  IssueCreate,
  SelfNoteCreate,
  RoleCommentCreate,
  AnnotationUpdate,
  StatusChangeRequest,
  CommentCreate,
  LinkCreate,
  BulkStatusChangeRequest,
  BulkAssignRequest,
  BulkMarkReadRequest,
} from '@/types/annotation'

// =============================================================================
// Query Keys
// =============================================================================

export const annotationKeys = {
  all: ['annotations'] as const,
  lists: () => [...annotationKeys.all, 'list'] as const,
  list: (studyId: string, filters?: AnnotationFilters) =>
    [...annotationKeys.lists(), studyId, filters] as const,
  details: () => [...annotationKeys.all, 'detail'] as const,
  detail: (studyId: string, id: number) =>
    [...annotationKeys.details(), studyId, id] as const,
  comments: (studyId: string, annotationId: number) =>
    [...annotationKeys.all, 'comments', studyId, annotationId] as const,
  attachments: (studyId: string, annotationId: number) =>
    [...annotationKeys.all, 'attachments', studyId, annotationId] as const,
  links: (studyId: string, annotationId: number) =>
    [...annotationKeys.all, 'links', studyId, annotationId] as const,
  history: (studyId: string, annotationId: number) =>
    [...annotationKeys.all, 'history', studyId, annotationId] as const,
  valueComparison: (studyId: string, annotationId: number) =>
    [...annotationKeys.all, 'valueComparison', studyId, annotationId] as const,
  snapshotComparison: (studyId: string, annotationId: number) =>
    [...annotationKeys.all, 'snapshotComparison', studyId, annotationId] as const,
  recordCounts: (studyId: string, datasetName: string) =>
    [...annotationKeys.all, 'recordCounts', studyId, datasetName] as const,
  selfNotes: (studyId: string) =>
    [...annotationKeys.all, 'selfNotes', studyId] as const,
  roleComments: (studyId: string) =>
    [...annotationKeys.all, 'roleComments', studyId] as const,
  metrics: () => [...annotationKeys.all, 'metrics'] as const,
  metricsSummary: (studyId: string) =>
    [...annotationKeys.metrics(), 'summary', studyId] as const,
  agingReport: (studyId: string) =>
    [...annotationKeys.metrics(), 'aging', studyId] as const,
  byGroup: (studyId: string, groupBy: string) =>
    [...annotationKeys.metrics(), 'byGroup', studyId, groupBy] as const,
  turnaround: (studyId: string) =>
    [...annotationKeys.metrics(), 'turnaround', studyId] as const,
  userMetrics: (studyId: string) =>
    [...annotationKeys.metrics(), 'user', studyId] as const,
}

// =============================================================================
// Issues Query Hooks
// =============================================================================

/**
 * Fetch paginated issues list.
 */
export function useIssues(
  studyId: string | undefined,
  options?: {
    page?: number
    pageSize?: number
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
    filters?: AnnotationFilters
    enabled?: boolean
  }
) {
  return useQuery({
    queryKey: annotationKeys.list(studyId!, options?.filters),
    queryFn: () =>
      annotationApi.listIssues(studyId!, {
        page: options?.page,
        pageSize: options?.pageSize,
        sortBy: options?.sortBy,
        sortOrder: options?.sortOrder,
        filters: options?.filters,
      }),
    enabled: !!studyId && options?.enabled !== false,
    staleTime: 30 * 1000, // 30 seconds
  })
}

/**
 * Fetch a single issue by ID.
 */
export function useIssue(studyId: string | undefined, annotationId: number | null) {
  return useQuery({
    queryKey: annotationKeys.detail(studyId!, annotationId!),
    queryFn: () => annotationApi.getIssue(studyId!, annotationId!),
    enabled: !!studyId && !!annotationId,
  })
}

/**
 * Fetch any annotation by ID (issue, self_note, or role_comment).
 * Uses the generic /detail/ endpoint instead of the issues-only endpoint.
 */
export function useAnnotation(studyId: string | undefined, annotationId: number | null) {
  return useQuery({
    queryKey: annotationKeys.detail(studyId!, annotationId!),
    queryFn: () => annotationApi.getAnnotation(studyId!, annotationId!),
    enabled: !!studyId && !!annotationId,
  })
}

/**
 * Create an issue mutation.
 */
export function useCreateIssue(studyId: string | undefined) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: IssueCreate) => {
      if (!studyId) throw new Error('Study ID is required')
      return annotationApi.createIssue(studyId, data)
    },
    onSuccess: () => {
      // Invalidate issues list
      queryClient.invalidateQueries({ queryKey: annotationKeys.lists() })
      // Invalidate metrics
      queryClient.invalidateQueries({ queryKey: annotationKeys.metrics() })
      // Invalidate record counts for row highlighting in data tables
      queryClient.invalidateQueries({ queryKey: [...annotationKeys.all, 'recordCounts'] })
      toast({
        title: 'Issue created',
        description: 'The issue has been created successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create issue',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

/**
 * Update an issue mutation.
 */
export function useUpdateIssue(studyId: string | undefined) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({
      annotationId,
      data,
    }: {
      annotationId: number
      data: AnnotationUpdate
    }) => {
      if (!studyId) throw new Error('Study ID is required')
      return annotationApi.updateIssue(studyId, annotationId, data)
    },
    onSuccess: (data) => {
      // Update cache with new data
      queryClient.setQueryData(annotationKeys.detail(studyId!, data.id), data)
      // Invalidate list
      queryClient.invalidateQueries({ queryKey: annotationKeys.lists() })
      toast({
        title: 'Issue updated',
        description: 'The issue has been updated successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update issue',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

/**
 * Change issue status mutation.
 */
export function useChangeIssueStatus(studyId: string | undefined) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({
      annotationId,
      data,
    }: {
      annotationId: number
      data: StatusChangeRequest
    }) => {
      if (!studyId) throw new Error('Study ID is required')
      return annotationApi.changeIssueStatus(studyId, annotationId, data)
    },
    onSuccess: (data) => {
      queryClient.setQueryData(annotationKeys.detail(studyId!, data.id), data)
      queryClient.invalidateQueries({ queryKey: annotationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: annotationKeys.metrics() })
      // Invalidate record counts for row highlighting (open issue count changes)
      queryClient.invalidateQueries({ queryKey: [...annotationKeys.all, 'recordCounts'] })
      toast({
        title: 'Status changed',
        description: `Issue status changed to ${data.status}.`,
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to change status',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

/**
 * Delete an issue mutation.
 */
export function useDeleteIssue(studyId: string | undefined) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (annotationId: number) => {
      if (!studyId) throw new Error('Study ID is required')
      return annotationApi.deleteIssue(studyId, annotationId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: annotationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: annotationKeys.metrics() })
      // Invalidate record counts for row highlighting
      queryClient.invalidateQueries({ queryKey: [...annotationKeys.all, 'recordCounts'] })
      toast({
        title: 'Issue deleted',
        description: 'The issue has been deleted.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete issue',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

// =============================================================================
// Self-Notes Hooks
// =============================================================================

/**
 * Fetch self-notes for the current user.
 */
export function useSelfNotes(
  studyId: string | undefined,
  options?: {
    page?: number
    pageSize?: number
    datasetName?: string
    searchText?: string
  }
) {
  return useQuery({
    queryKey: annotationKeys.selfNotes(studyId!),
    queryFn: () => annotationApi.listSelfNotes(studyId!, options),
    enabled: !!studyId,
    staleTime: 30 * 1000,
  })
}

/**
 * Create a self-note mutation.
 */
export function useCreateSelfNote(studyId: string | undefined) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: SelfNoteCreate) => {
      if (!studyId) throw new Error('Study ID is required')
      return annotationApi.createSelfNote(studyId, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: annotationKeys.selfNotes(studyId!) })
      toast({
        title: 'Note saved',
        description: 'Your personal note has been saved.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to save note',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

// =============================================================================
// Role Comments Hooks
// =============================================================================

/**
 * Fetch role comments visible to the current user.
 */
export function useRoleComments(
  studyId: string | undefined,
  options?: {
    page?: number
    pageSize?: number
    datasetName?: string
    targetRole?: string
    searchText?: string
  }
) {
  return useQuery({
    queryKey: annotationKeys.roleComments(studyId!),
    queryFn: () => annotationApi.listRoleComments(studyId!, options),
    enabled: !!studyId,
    staleTime: 30 * 1000,
  })
}

/**
 * Create a role comment mutation.
 */
export function useCreateRoleComment(studyId: string | undefined) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: RoleCommentCreate) => {
      if (!studyId) throw new Error('Study ID is required')
      return annotationApi.createRoleComment(studyId, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: annotationKeys.roleComments(studyId!) })
      toast({
        title: 'Comment saved',
        description: 'Your role comment has been saved.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to save comment',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

// =============================================================================
// Comments Hooks
// =============================================================================

/**
 * Fetch comments for an annotation.
 */
export function useComments(studyId: string | undefined, annotationId: number | null) {
  return useQuery({
    queryKey: annotationKeys.comments(studyId!, annotationId!),
    queryFn: () => annotationApi.listComments(studyId!, annotationId!),
    enabled: !!studyId && !!annotationId,
  })
}

/**
 * Add a comment mutation.
 */
export function useAddComment(studyId: string | undefined, annotationId: number) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: CommentCreate) => {
      if (!studyId) throw new Error('Study ID is required')
      return annotationApi.addComment(studyId, annotationId, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: annotationKeys.comments(studyId!, annotationId),
      })
      queryClient.invalidateQueries({
        queryKey: annotationKeys.detail(studyId!, annotationId),
      })
      toast({
        title: 'Comment added',
        description: 'Your comment has been added.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to add comment',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

/**
 * Delete a comment mutation.
 */
export function useDeleteComment(studyId: string | undefined, annotationId: number) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (commentId: number) => {
      if (!studyId) throw new Error('Study ID is required')
      return annotationApi.deleteComment(studyId, annotationId, commentId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: annotationKeys.comments(studyId!, annotationId),
      })
      toast({
        title: 'Comment deleted',
        description: 'The comment has been deleted.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete comment',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

// =============================================================================
// Attachments Hooks
// =============================================================================

/**
 * Fetch attachments for an annotation.
 */
export function useAttachments(studyId: string | undefined, annotationId: number | null) {
  return useQuery({
    queryKey: annotationKeys.attachments(studyId!, annotationId!),
    queryFn: () => annotationApi.listAttachments(studyId!, annotationId!),
    enabled: !!studyId && !!annotationId,
  })
}

/**
 * Upload an attachment mutation.
 */
export function useUploadAttachment(studyId: string | undefined, annotationId: number) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (file: File) => {
      if (!studyId) throw new Error('Study ID is required')
      return annotationApi.uploadAttachment(studyId, annotationId, file)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: annotationKeys.attachments(studyId!, annotationId),
      })
      queryClient.invalidateQueries({
        queryKey: annotationKeys.detail(studyId!, annotationId),
      })
      toast({
        title: 'File uploaded',
        description: 'The attachment has been uploaded.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to upload file',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

/**
 * Delete an attachment mutation.
 */
export function useDeleteAttachment(studyId: string | undefined, annotationId: number) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (attachmentId: number) => {
      if (!studyId) throw new Error('Study ID is required')
      return annotationApi.deleteAttachment(studyId, annotationId, attachmentId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: annotationKeys.attachments(studyId!, annotationId),
      })
      toast({
        title: 'Attachment deleted',
        description: 'The attachment has been deleted.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete attachment',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

// =============================================================================
// Links Hooks
// =============================================================================

/**
 * Fetch links for an annotation.
 */
export function useLinks(studyId: string | undefined, annotationId: number | null) {
  return useQuery({
    queryKey: annotationKeys.links(studyId!, annotationId!),
    queryFn: () => annotationApi.listLinks(studyId!, annotationId!),
    enabled: !!studyId && !!annotationId,
  })
}

/**
 * Create a link mutation.
 */
export function useCreateLink(studyId: string | undefined, annotationId: number) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: LinkCreate) => {
      if (!studyId) throw new Error('Study ID is required')
      return annotationApi.createLink(studyId, annotationId, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: annotationKeys.links(studyId!, annotationId),
      })
      queryClient.invalidateQueries({
        queryKey: annotationKeys.detail(studyId!, annotationId),
      })
      toast({
        title: 'Link created',
        description: 'The issues have been linked.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create link',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

/**
 * Delete a link mutation.
 */
export function useDeleteLink(studyId: string | undefined, annotationId: number) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (linkId: number) => {
      if (!studyId) throw new Error('Study ID is required')
      return annotationApi.deleteLink(studyId, annotationId, linkId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: annotationKeys.links(studyId!, annotationId),
      })
      toast({
        title: 'Link removed',
        description: 'The link has been removed.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to remove link',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

// =============================================================================
// History Hook
// =============================================================================

/**
 * Fetch history for an annotation.
 */
export function useHistory(studyId: string | undefined, annotationId: number | null) {
  return useQuery({
    queryKey: annotationKeys.history(studyId!, annotationId!),
    queryFn: () => annotationApi.getHistory(studyId!, annotationId!),
    enabled: !!studyId && !!annotationId,
  })
}

// =============================================================================
// Value Comparison Hook
// =============================================================================

/**
 * Fetch value comparison for an annotation.
 */
export function useValueComparison(studyId: string | undefined, annotationId: number | null) {
  return useQuery({
    queryKey: annotationKeys.valueComparison(studyId!, annotationId!),
    queryFn: () => annotationApi.getValueComparison(studyId!, annotationId!),
    enabled: !!studyId && !!annotationId,
  })
}

// =============================================================================
// Snapshot Comparison Hook
// =============================================================================

/**
 * Fetch snapshot comparison (creation vs current data) for an annotation.
 */
export function useSnapshotComparison(studyId: string | undefined, annotationId: number | null) {
  return useQuery({
    queryKey: annotationKeys.snapshotComparison(studyId!, annotationId!),
    queryFn: () => annotationApi.getSnapshotComparison(studyId!, annotationId!),
    enabled: !!studyId && !!annotationId,
  })
}

// =============================================================================
// Bulk Operations Hooks
// =============================================================================

/**
 * Bulk change status mutation.
 */
export function useBulkChangeStatus(studyId: string | undefined) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: BulkStatusChangeRequest) => {
      if (!studyId) throw new Error('Study ID is required')
      return annotationApi.bulkChangeStatus(studyId, data)
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: annotationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: annotationKeys.metrics() })
      // Invalidate record counts for row highlighting
      queryClient.invalidateQueries({ queryKey: [...annotationKeys.all, 'recordCounts'] })
      toast({
        title: 'Status updated',
        description: `${result.successCount} issues updated successfully.`,
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Bulk operation failed',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

/**
 * Bulk assign mutation.
 */
export function useBulkAssign(studyId: string | undefined) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: BulkAssignRequest) => {
      if (!studyId) throw new Error('Study ID is required')
      return annotationApi.bulkAssign(studyId, data)
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: annotationKeys.lists() })
      toast({
        title: 'Issues assigned',
        description: `${result.successCount} issues assigned successfully.`,
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Bulk assign failed',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

/**
 * Bulk mark as read mutation.
 */
export function useBulkMarkRead(studyId: string | undefined) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: BulkMarkReadRequest) => {
      if (!studyId) throw new Error('Study ID is required')
      return annotationApi.bulkMarkRead(studyId, data)
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: annotationKeys.lists() })
      toast({
        title: 'Marked as read',
        description: `${result.successCount} issues marked as read.`,
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Operation failed',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

// =============================================================================
// Record Counts Hook
// =============================================================================

/**
 * Fetch annotation counts for multiple records.
 * Useful for showing issue indicators in data tables.
 */
export function useAnnotationCounts(
  studyId: string | undefined,
  datasetName: string | undefined,
  recordKeys: Record<string, unknown>[]
) {
  return useQuery({
    queryKey: [...annotationKeys.recordCounts(studyId!, datasetName!), recordKeys],
    queryFn: () =>
      annotationApi.getRecordCounts(studyId!, {
        datasetName: datasetName!,
        recordKeys,
      }),
    enabled: !!studyId && !!datasetName && recordKeys.length > 0,
    staleTime: 60 * 1000, // 1 minute
  })
}

// =============================================================================
// Module Observations Hook
// =============================================================================

/**
 * Fetch module-level observations (issues with empty recordKey) for a specific module.
 * Used by ModuleIssueButton to show observation count badge and observations panel.
 */
export function useModuleObservations(
  studyId: string | undefined,
  moduleKey: string | undefined,
  options?: { enabled?: boolean }
) {
  const filters: AnnotationFilters = {
    sourceModuleKey: moduleKey,
    annotationTypes: ['issue'],
  }

  return useQuery({
    queryKey: [...annotationKeys.lists(), 'moduleObservations', studyId, moduleKey],
    queryFn: () =>
      annotationApi.listIssues(studyId!, {
        page: 1,
        pageSize: 50,
        sortBy: 'created_at',
        sortOrder: 'desc',
        filters,
      }),
    enabled: !!studyId && !!moduleKey && options?.enabled !== false,
    staleTime: 60 * 1000, // 1 minute
  })
}

// =============================================================================
// Mark As Read Hook
// =============================================================================

/**
 * Mark an annotation as read mutation.
 */
export function useMarkAsRead(studyId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (annotationId: number) => {
      if (!studyId) throw new Error('Study ID is required')
      return annotationApi.markAsRead(studyId, annotationId)
    },
    onSuccess: (data) => {
      queryClient.setQueryData(annotationKeys.detail(studyId!, data.id), data)
      queryClient.invalidateQueries({ queryKey: annotationKeys.lists() })
    },
  })
}

// =============================================================================
// Metrics Hooks
// =============================================================================

/**
 * Fetch issue metrics summary.
 */
export function useIssueMetrics(studyId: string | undefined) {
  return useQuery({
    queryKey: annotationKeys.metricsSummary(studyId!),
    queryFn: () => annotationApi.getMetricsSummary(studyId!),
    enabled: !!studyId,
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Fetch aging report.
 */
export function useAgingReport(studyId: string | undefined) {
  return useQuery({
    queryKey: annotationKeys.agingReport(studyId!),
    queryFn: () => annotationApi.getAgingReport(studyId!),
    enabled: !!studyId,
    staleTime: 60 * 1000,
  })
}

/**
 * Fetch issues by group.
 */
export function useIssuesByGroup(
  studyId: string | undefined,
  groupBy: 'site' | 'assignee' | 'module' | 'category' | 'priority'
) {
  return useQuery({
    queryKey: annotationKeys.byGroup(studyId!, groupBy),
    queryFn: () => annotationApi.getIssuesByGroup(studyId!, groupBy),
    enabled: !!studyId,
    staleTime: 60 * 1000,
  })
}

/**
 * Fetch turnaround report.
 */
export function useTurnaroundReport(studyId: string | undefined) {
  return useQuery({
    queryKey: annotationKeys.turnaround(studyId!),
    queryFn: () => annotationApi.getTurnaroundReport(studyId!),
    enabled: !!studyId,
    staleTime: 60 * 1000,
  })
}

/**
 * Fetch user-specific issue metrics.
 */
export function useUserIssueMetrics(studyId: string | undefined) {
  return useQuery({
    queryKey: annotationKeys.userMetrics(studyId!),
    queryFn: () => annotationApi.getUserMetrics(studyId!),
    enabled: !!studyId,
    staleTime: 60 * 1000,
  })
}

// =============================================================================
// Export Hook
// =============================================================================

/**
 * Export issues to CSV mutation.
 */
export function useExportIssuesCsv(studyId: string | undefined) {
  const { toast } = useToast()

  return useMutation({
    mutationFn: (filters?: AnnotationFilters) => {
      if (!studyId) throw new Error('Study ID is required')
      return annotationApi.exportIssuesCsv(studyId, filters)
    },
    onSuccess: (blob) => {
      // Create download link
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `issues-export-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      toast({
        title: 'Export complete',
        description: 'Issues have been exported to CSV.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Export failed',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

/**
 * Get single issue export data (for PDF).
 */
export function useSingleIssueExport(studyId: string | undefined, annotationId: number | null) {
  return useQuery({
    queryKey: [...annotationKeys.detail(studyId!, annotationId!), 'export'],
    queryFn: () => annotationApi.getSingleIssueExportData(studyId!, annotationId!),
    enabled: !!studyId && !!annotationId,
  })
}
